// utils/urlGuards.js (or inline in each file)

const LOOPBACK_HOSTNAMES = new Set([
  "localhost",
  "ip6-localhost",
  "ip6-loopback",
  "localhost.localdomain",
]);

/**
 * Mirror of the backend's _validate_outbound_url.
 * Returns null if the URL looks saveable, or a human-readable error string.
 *
 * This is a UX pre-check, not the source of truth. The backend validates
 * the same rules authoritatively — this just saves a round-trip and gives
 * the operator inline feedback. Never rely on this for security.
 *
 * We can't do the full RFC-1918 / link-local / loopback-mask checks that
 * the Python side does via `ipaddress.ip_address`, because browsers don't
 * expose an equivalent. So we approximate:
 *   - named loopback hostnames → exact match
 *   - IPv4 loopback / private / link-local → regex on the octets
 *   - IPv6 loopback / link-local / unspecified → regex prefix match
 *
 * False negatives (URLs the backend would reject but we accept) are fine —
 * the backend catches them. False positives (URLs the backend would accept
 * but we reject) are NOT fine — so every check here must be conservative.
 */
export const checkOutboundUrl = (url) => {
  if (!url || typeof url !== "string") {
    return "URL is empty";
  }

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    // Let the backend own the "malformed URL" message.
    return null;
  }

  const host = (parsed.hostname || "").toLowerCase();

  if (LOOPBACK_HOSTNAMES.has(host)) {
    return (
      `'${host}' points at the server itself and cannot be reached from our ` +
      `infrastructure. Please use a publicly reachable URL.`
    );
  }

  // IPv4 literal checks
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    if (/^127\./.test(host)) {
      return `IP address ${host} is a loopback address and cannot be reached from our infrastructure.`;
    }
    if (host === "0.0.0.0") {
      return `IP address ${host} is the unspecified address and cannot be reached from our infrastructure.`;
    }
    if (/^169\.254\./.test(host)) {
      return `IP address ${host} is a link-local address and cannot be reached from our infrastructure.`;
    }
    if (
      /^10\./.test(host) ||
      /^192\.168\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host)
    ) {
      return (
        `IP address ${host} is a private address and cannot be reached from ` +
        `our infrastructure. Please use a publicly reachable URL.`
      );
    }
  }

  // IPv6 literal checks. `URL.hostname` strips the square brackets, so a
  // bracketed literal like http://[::1]:3000/ becomes "::1".
  const v6 = host;
  if (v6 === "::1") {
    return `IP address ${v6} is a loopback address and cannot be reached from our infrastructure.`;
  }
  if (v6 === "::") {
    return `IP address ${v6} is the unspecified address and cannot be reached from our infrastructure.`;
  }
  if (/^fe80:/i.test(v6) || /^f[cd][0-9a-f]{2}:/i.test(v6)) {
    return `IP address ${v6} is a link-local or unique-local address and cannot be reached from our infrastructure.`;
  }

  return null;
};